export interface IConfigurationProfile<
  Specific extends
    | E3PlusConfig
    | E3Plus4GConfig
    | BwsNb2Config
    | BwsLoraConfig
    | Bws4GConfig
    | Bws4GLoraConfig
    | BwsLlNbConfig =
    | E3PlusConfig
    | E3Plus4GConfig
    | BwsNb2Config
    | BwsLoraConfig
    | Bws4GConfig
    | Bws4GLoraConfig
    | BwsLlNbConfig,
> {
  id: string;
  sequential_id?: number;
  name: string;
  type: EType;
  config: Config<Specific>;
  created_at: Date;
  user_id: string;
  validation: Validation;
  technology_id: string;
  technology_system_name: DeviceModel;
  client_id?: string;
  manual_client?: string;
  manual_client_register?: boolean;
  status?: ConfigurationProfileStatus;
  updated_at?: Date;
  validated_at?: Date | null;
  inactivated_at?: Date | null;
  technology?: {
    id: string;
    name: {
      brand: string;
      system: DeviceModel;
    };
  };
  client?: {
    id: string;
    trade_name?: string;
    company_name?: string;
    document?: {
      value: string;
    };
  };
}

export type ConfigurationProfileStatus = 'ATIVO' | 'INATIVO' | 'AGUARDANDO_VALIDACAO';

export type ConfigurationProfileHistoryAction =
  | 'CREATED'
  | 'UPDATED'
  | 'VALIDATED'
  | 'INACTIVATED'
  | 'REPLACED';

export interface IConfigurationProfileHistory {
  id: string;
  configuration_profile_id: string;
  action: ConfigurationProfileHistoryAction;
  description: string;
  user_id?: string | null;
  user_email?: string | null;
  user_name?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at: Date;
}

export interface Check {
  raw_check?: string;
  normalized_check?: string;
}

export interface Config<Specific> {
  general?: GeneralConfig;
  specific?: Specific;
  check?: Check;
}

interface Validation {
  by_human: boolean;
  by_system: boolean;
}

export const EType = {
  HUMAN: 'HUMAN',
  TRUCK: 'TRUCK',
  MOTORCYCLE: 'MOTORCYCLE',
  STUFF: 'STUFF',
  BOAT: 'BOAT',
  CAR: 'CAR',
  BUS: 'BUS',
  UTILITY_VEHICLE: 'UTILITY_VEHICLE',
  BIKE: 'BIKE',
  ANIMAL: 'ANIMAL',
  ROAD_IMPLEMENT: 'ROAD_IMPLEMENT',
  FARM_IMPLEMENT: 'FARM_IMPLEMENT',
  JET: 'JET',
  JET_SKI: 'JET_SKI',
  AIRCRAFT: 'AIRCRAFT',
  OTHER: 'OTHER',
} as const;

export type EType = (typeof EType)[keyof typeof EType];

export const DeviceModel = {
  DM_E3_PLUS: 'DM_E3_PLUS',
  DM_E3_PLUS_PERSONAL: 'DM_E3_PLUS_PERSONAL',
  DM_E3_PLUS_PET: 'DM_E3_PLUS_PET',
  DM_E3_PLUS_OBD_II: 'DM_E3_PLUS_OBD_II',
  DM_E3_PLUS_LONG_LIFE: 'DM_E3_PLUS_LONG_LIFE',
  DM_E3: 'DM_E3',
  DM_GT06: 'DM_GT06',
  DM_E3_PLUS_4G: 'DM_E3_PLUS_4G',
  DM_BWS_PLUS_4G_LORA: 'DM_BWS_PLUS_4G_LORA',
  DM_BWS_NB2: 'DM_BWS_NB2',
  DM_BWS_NB2_LORA: 'DM_BWS_NB2_LORA',
  DM_BWS_LORA: 'DM_BWS_LORA',
  DM_BWS_4G: 'DM_BWS_4G',
  DM_BWS_BUSCADOR: 'DM_BWS_BUSCADOR',
  DM_BWS_LL_NB: 'DM_BWS_LL_NB',
  DM_BWS_LL_NB_LORA: 'DM_BWS_LL_NB_LORA',
  DM_BWS_BAIT_NB: 'DM_BWS_BAIT_NB',
  DM_BWS_BAIT_NB_LORA: 'DM_BWS_BAIT_NB_LORA',
  UNRECOGNIZED: 'UNRECOGNIZED',
} as const;

export type DeviceModel = (typeof DeviceModel)[keyof typeof DeviceModel];

type DNS = {
  address: string;
  port: number;
};

type IP = {
  ip: string;
  port: number;
};

type APN = {
  address: string;
  user: string;
  password?: string | undefined;
};

type GeneralConfig = {
  ip_primary?: IP | undefined;
  ip_secondary?: IP | undefined;
  dns_primary?: DNS | undefined;
  dns_secondary?: DNS | undefined;
  apn?: APN | undefined;
  data_transmission_on?: number | undefined;
  data_transmission_off?: number | undefined;
  keep_alive?: number | undefined;
};

export type E3PlusConfig = {
  password?:
    | {
        old?: string | undefined;
        new?: string | undefined;
      }
    | undefined;
  timezone?: number | undefined;
  lock_type?: number | undefined;
  odometer?: number | undefined;
  accelerometer_sensitivity?: number | undefined;
  economy_mode?: number | undefined;
  sensitivity_adjustment?: number | undefined;
  lbs_position?: boolean | undefined;
  cornering_position_update?: boolean | undefined;
  ignition_alert_power_cut?: boolean | undefined;
  gprs_failure_alert?: boolean | undefined;
  led?: boolean | undefined;
  virtual_ignition?: boolean | undefined;
  work_mode?: string | undefined;
  operation_mode?: boolean | undefined;
  optional_functions?: Record<string, boolean> | undefined;
  max_speed?: number | undefined;
  sleep?: number | undefined;
};

export type E3Plus4GConfig = {
  password?:
    | {
        old?: string | undefined;
        new?: string | undefined;
      }
    | undefined;
  timezone?: number | undefined;
  lock_type?: number | undefined;
  odometer?: number | undefined;
  economy_mode?: number | undefined;
  sensitivity_adjustment?: number | undefined;
  lbs_position?: boolean | undefined;
  cornering_position_update?: boolean | undefined;
  led?: boolean | undefined;
  virtual_ignition?: boolean | undefined;
  virtual_ignition_by_voltage?: boolean | undefined;
  virtual_ignition_by_movement?: boolean | undefined;
  optional_functions?: Record<string, boolean>;
  max_speed?: number | undefined;
  communication_type?: string | undefined;
  protocol_type?: string | undefined;
  anti_theft?: boolean | undefined;
  horimeter?: number | undefined;
  jammer_detection?: boolean | undefined;
  clear_buffer?: boolean | undefined;
  clear_horimeter?: boolean | undefined;
  input_1?: number | undefined;
  input_2?: number | undefined;
  angle_adjustment?: number | undefined;
  lock_type_progression?: {
    n1: number | undefined;
    n2: number | undefined;
  };
  ignition_by_voltage?: {
    initial: number | undefined;
    final: number | undefined;
  };
  ack?: number | undefined;
  ignition_status_hb?: boolean | undefined;
};

export type BwsNb2Config = {
  data_transmission_event?: number | undefined;
  time_to_sleep?: number | undefined;
  odometer?: number | undefined;
  virtual_ignition_12v?: { initial: number | undefined; final: number } | undefined;
  virtual_ignition_24v?: { initial: number | undefined; final: number } | undefined;
  heading_detection_angle?: number | undefined;
  speed_alert_threshold?: number | undefined;
  accel_threshold_for_ignition_on?: number | undefined;
  accel_threshold_for_ignition_off?: number | undefined;
  accel_threshold_for_movement?: number | undefined;
  harsh_acceleration_threshold?: number | undefined;
  harsh_braking_threshold?: number | undefined;
  input_1?: string | undefined;
  input_2?: string | undefined;
  input_3?: string | undefined;
  input_4?: string | undefined;
  full_configuration_table?: string | undefined;
  full_functionality_table?: string | undefined;
  sleep_mode?: string | undefined;
  lock_type?: number | undefined;
};

export type BwsLoraConfig = {
  odometer?: number | undefined;
  data_transmission_sleep?: number | undefined;
  virtual_ignition_12v?: { initial: number | undefined; final: number } | undefined;
  virtual_ignition_24v?: { initial: number | undefined; final: number } | undefined;
  heading?: boolean | undefined;
  heading_event_mode?: boolean | undefined;
  heading_detection_angle?: number | undefined;
  speed_alert_threshold?: number | undefined;
  accel_threshold_for_ignition_on?: number | undefined;
  accel_threshold_for_ignition_off?: number | undefined;
  accel_threshold_for_movement?: number | undefined;
  harsh_acceleration_threshold?: number | undefined;
  harsh_braking_threshold?: number | undefined;
  data_transmission_position?: number | undefined;
  led_lighting?: string | undefined;
  p2p_mode_duration?: number | undefined;
  lorawan_mode_duration?: number | undefined;
  input_1?: string | undefined;
  input_2?: string | undefined;
  input_3?: string | undefined;
  input_4?: string | undefined;
  input_5?: string | undefined;
  input_6?: string | undefined;
  full_configuration_table?: string | undefined;
  fifo_send_and_hold_times?: number[] | undefined;
  lorawan_data_transmission_event?: number | undefined;
  p2p_data_transmission_event?: number | undefined;
  data_transmission_status?: number | undefined;
  full_functionality_table?: string | undefined;
  activation_type?: string | undefined;
  mcu_configuration?: string | undefined;
  output_table?: number[] | undefined;
};

export type Bws4GConfig = {
  password?:
    | {
        old?: string | undefined;
        new?: string | undefined;
      }
    | undefined;
  lock_type?: number | undefined;
  lock_type_variation?: number | undefined;
  odometer?: number | undefined;
  sensitivity_adjustment?: number | undefined;
  lbs_position?: boolean | undefined;
  led?: boolean | undefined;
  optional_functions?: Record<string, boolean>;
  max_speed?: number | undefined;
  communication_type?: string | undefined;
  protocol_type?: string | undefined;
  anti_theft?: boolean | undefined;
  horimeter?: number | undefined;
  jammer_detection?: boolean | undefined;
  clear_buffer?: boolean | undefined;
  clear_horimeter?: boolean | undefined;
  input_1?: number | undefined;
  input_2?: number | undefined;
  angle_adjustment?: number | undefined;
  ignition_status_hb?: boolean | undefined;
  sms?: boolean | undefined;
  gps_filter_speed?: boolean | undefined;
  gps_periodic_wakeup?: boolean | undefined;
  always_get_agps?: boolean | undefined;
  ignition_type?: number | undefined;
  economy_mode?: number | undefined;
  network_mode?: 'AUTO' | '4G' | '2G' | undefined;
  lock_type_progression?:
    | {
        n1: number;
        n2: number;
      }
    | undefined;
  gps_replay_fix_sleep?: boolean | undefined;
  improper_movement?: boolean | undefined;
};

// Cópia do BWS 4G — vai divergir quando a parte LoRa for implementada
export type Bws4GLoraConfig = {
  password?:
    | {
        old?: string | undefined;
        new?: string | undefined;
      }
    | undefined;
  lock_type?: number | undefined;
  lock_type_variation?: number | undefined;
  odometer?: number | undefined;
  sensitivity_adjustment?: number | undefined;
  lbs_position?: boolean | undefined;
  led?: boolean | undefined;
  optional_functions?: Record<string, boolean>;
  max_speed?: number | undefined;
  communication_type?: string | undefined;
  protocol_type?: string | undefined;
  anti_theft?: boolean | undefined;
  horimeter?: number | undefined;
  jammer_detection?: boolean | undefined;
  clear_buffer?: boolean | undefined;
  clear_horimeter?: boolean | undefined;
  input_1?: number | undefined;
  input_2?: number | undefined;
  angle_adjustment?: number | undefined;
  ignition_status_hb?: boolean | undefined;
  sms?: boolean | undefined;
  gps_filter_speed?: boolean | undefined;
  gps_periodic_wakeup?: boolean | undefined;
  always_get_agps?: boolean | undefined;
  ignition_type?: number | undefined;
  economy_mode?: number | undefined;
  network_mode?: 'AUTO' | '4G' | '2G' | undefined;
  lock_type_progression?:
    | {
        n1: number;
        n2: number;
      }
    | undefined;
  gps_replay_fix_sleep?: boolean | undefined;
  improper_movement?: boolean | undefined;
};

/**
 * BWS LL NB IoT+LoRa (manual v1.0.0). Intervalos seguem as unidades do firmware:
 * minutos para movimento/parado/estoque/desabilitado/timeout de SOS, segundos para SOS e FIX.
 */
export type BwsLlNbConfig = {
  data_transmission_moving?: number | undefined; // WCM (min)
  data_transmission_stopped?: number | undefined; // WCW (min)
  data_transmission_sos?: number | undefined; // WCE (seg)
  data_transmission_stock?: number | undefined; // WCTE (min)
  data_transmission_disabled?: number | undefined; // WCD (min)
  gps_every_cycle?: boolean | undefined; // WFGO
  gps_fix_timeout?: number | undefined; // WCG (seg)
  work_mode?: number | undefined; // WFOM (1 estoque, 2 normal, 128 desativado)
  sos_timeout?: number | undefined; // WFST (min)
  power_string?: boolean | undefined; // WFPO
  radio_behavior?: // WBTR=w,x,y,z
    | {
        no_connection_timeout: number; // w (min)
        stopped_cycle: number; // x
        moving_cycle: number; // y
        flags: number; // z (37, 41 ou 45)
      }
    | undefined;
};
